import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import styles from '../styles/BlogDetail.module.css';
import Sidebar from './Sidebar';
import Loader from '../components/Loader';
import { Send,
  RefreshCw,
  MessageSquare,
  Heart,
  Share2,
  Edit,
  Trash2,
  Image,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Link,
  Key,  // Changed from 'key' to 'Key'
  CircleUser,
  Home,
  LineChart,
  MessagesSquare } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { stripHtmlTags } from '../utils/HtmlStrip';
import { formatIndianDate } from '../utils/timeUtils';

const BlogDetail = () => {
  const { id } = useParams(); // We only need the id
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user data if token exists
        const token = localStorage.getItem('token');
        if (token) {
          const userResponse = await axiosInstance.get('/auth/verify-token');
          setUser(userResponse.data.user);
        }

        // Fetch blog data
        const response = await axiosInstance.get(`/blogs/detail/${id}`);
        if (response.data.success && response.data.data) {
          setBlog(response.data.data);
        } else {
          setError('Blog not found');
        }
      } catch (error) {
        setError(error.response?.data?.message || 'Error loading blog');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  useEffect(() => {
    if (blog) {
      document.title = `Stockkhata | ${blog.title}`;
    }
  }, [blog]);

  // Update getNavItems to include icons
  const getNavItems = (user) => {
    if (!user || user.name === 'Guest') {
      return [
        { name: 'Home', icon: <Home size={20} />, path: '/', ariaLabel: 'Home' },
        { name: 'Thoughts', icon: <MessagesSquare size={20} />, path: '/threads-blogs', ariaLabel: 'Community Threads & Blogs' },
        { name: 'Login', icon: <CircleUser size={20} />, path: '/login', ariaLabel: 'Login' },
        { name: 'Signup', icon: <Key size={20} />, path: '/signup', ariaLabel: 'Signup' },
      ];
    }
    return [
      { name: 'Dashboard', icon: <Home size={20} />, path: '/dashboard', ariaLabel: 'Dashboard' },
      { name: 'Analytics', icon: <LineChart size={20} />, path: '/analytics', ariaLabel: 'Analytics' },
      { name: 'Thoughts', icon: <MessagesSquare size={20} />, path: '/threads-blogs', ariaLabel: 'Community Threads & Blogs' },
      { name: 'Feedback', icon: <MessageSquare size={20} />, path: '/feedback', ariaLabel: 'Feedback' }
    ];
  };


  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const handleDeleteBlog = async () => {
    if (!window.confirm('Are you sure you want to delete this blog? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await axiosInstance.delete(`/blogs/delete/${id}`);
      if (response.data.success) {
        navigate('/threads-blogs', { replace: true });
        alert('Blog deleted successfully.');
      }
    } catch (error) {
      alert('Failed to delete blog. Please try again.');
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error || !blog) {
    return (
      <div className={styles.pageContainer}>
        <Sidebar 
          user={user || { name: 'Guest' }}
          onLogout={handleLogout}
          navItems={getNavItems(user)}
        />
        <main className={styles.mainContent}>
          <div className={styles.notFound}>
            <h2 className={styles.notFoundTitle}>Blog Not Found</h2>
            <p className={styles.notFoundText}>
              {error || "The blog you're looking for doesn't exist or has been removed."}
            </p>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <Helmet>
        <title>Stockkhata | {blog.title}</title>
        <meta name="description" content={stripHtmlTags(blog.content).substring(0, 150)} />
        <meta property="og:title" content={`Stockkhata | ${blog.title}`} />
        <meta property="og:description" content={stripHtmlTags(blog.content).substring(0, 150)} />
        <meta property="og:image" content={blog.image} /> {/* Direct S3 URL */}
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Stockkhata | ${blog.title}`} />
        <meta name="twitter:description" content={stripHtmlTags(blog.content).substring(0, 150)} />
        <meta name="twitter:image" content={blog.image} /> {/* Direct S3 URL */}
      </Helmet>
      <Sidebar 
        user={user || { name: 'Guest' }}
        onLogout={handleLogout}
        navItems={getNavItems(user)}
      />
      <main className={styles.mainContent}>
        <div className={styles.blogContainer}>
          {blog.image && (
            <div className={styles.imageContainer}>
              <img 
                src={blog.image} // Direct S3 URL
                alt={blog.title} 
                className={styles.image}
                onError={(e) => {
                  console.error("Failed to load blog image:", blog.image);
                  e.target.src = '/placeholder-image.jpg';
                }}
              />
              <div className={styles.overlay}>
                <h1 className={styles.title}>{blog.title}</h1>
              </div>
            </div>
          )}
          
          <div className={styles.content}>
            {!blog.image && <h1 className={styles.titleNoImage}>{blog.title}</h1>}
            <div className={styles.blogContent} dangerouslySetInnerHTML={{ __html: blog.content }} />
          </div>

          <div className={styles.metaContainer}>
            <div className={styles.authorContainer}>
              <div className={styles.authorAvatar}>
                {blog.author?.name?.charAt(0)}
              </div>
              <div className={styles.authorInfo}>
                <span className={styles.authorName}>{blog.author?.name}</span>
                <span className={styles.authorEmail}>{blog.author?.email}</span>
              </div>
            </div>
            <span className={styles.date}>
              {formatIndianDate(blog.created_at)}
            </span>
          </div>
        </div>

        <div className={styles.actions}>
          {/* Only show edit/delete buttons to the author */}
          {user && user._id && String(user._id) === String(blog.user_id) && (
            <div className={styles.actionButtons}>
              <button 
                className={styles.editButton}
                onClick={() => navigate(`/blog/edit/${blog._id}`)}
              >
                <Edit size={18} /> Edit Blog
              </button>
              <button 
                className={styles.deleteButton}
                onClick={handleDeleteBlog}
              >
                <Trash2 size={18} /> Delete Blog
              </button>
            </div>
          )}
          
          <button 
            className={styles.actionButton}
            onClick={() => {
              const url = window.location.href;
              navigator.clipboard.writeText(url);
              alert('Link copied to clipboard!');
            }}
          >
            <Share2 size={18} /> <span>Share</span>
          </button>
        </div>
      </main>
    </div>
  );
};

export default BlogDetail;